import React, { useState } from "react";
import { GENERIC_ACTION_ERROR } from "../../services/qboExceptionsService";

// One open exception of the QuickBooks connector (DEV-2442) and the actions
// support can take on it. Presentational: the page owns the list and runs the
// action through onAct(action[, extra]), which rejects with a plain message.

// reason_code -> a plain-English label. Codes come from the connector
// (cycle-scheduled-tasks lib/qbo_*.py); an unknown one is shown as is.
export const REASON_LABELS = {
  customer_ambiguous: "More than one QuickBooks customer matches",
  customer_not_found: "No matching QuickBooks customer",
  closed_period: "Dated in a closed QuickBooks period",
  duplicate_name: "QuickBooks says the name already exists",
  duplicate_document_number: "QuickBooks says the document number already exists",
  qbo_error: "QuickBooks rejected the posting",
  lookup_failed: "Could not check QuickBooks for an earlier post",
  duplicate_in_qbo: "May already be in QuickBooks",
  unreadable_timestamp: "The Stripe date could not be read",
  unsupported_currency: "Currency not supported",
  refund_manual: "Refund to book by hand",
  refund_failure_manual: "Failed refund to reverse by hand",
  payout_manual: "Payout to book by hand",
  payout_mismatch: "Payout amounts do not match",
  payout_missing_rows: "Payout is missing transactions",
  payout_reversal_manual: "Failed payout to reverse by hand",
};

// The one description of each action: the page's legend and the row's
// confirm step both read it. confirm marks the irreversible ones.
export const ACTIONS = {
  resolve: { label: "Resolve", help: "Booked by hand in QuickBooks. The connector will never post it.", confirm: true, done: "Resolved" },
  ignore: { label: "Ignore", help: "Nothing to book. The connector marks the record ignored and never posts it.", confirm: true, done: "Ignored" },
  retry: { label: "Retry", help: "The cause is fixed. The connector tries again on its next run.", done: "Marked for retry" },
  choose_customer: { label: "Link customer", help: "For an ambiguous customer match, pick the right QuickBooks customer.", done: "Customer linked" },
};

function fmtDateTime(iso) {
  return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// One formatter per currency. Not utils' formatCurrency: that one is USD-only,
// and Stripe amounts come in the transaction's own currency.
const currencyFormats = new Map();

function currencyFormat(currency) {
  if (!currencyFormats.has(currency)) {
    currencyFormats.set(currency, new Intl.NumberFormat("en-US", { style: "currency", currency }));
  }
  return currencyFormats.get(currency);
}

// The names the connector searched QuickBooks for (context.names_tried, in its
// order: the Stripe customer name, the billing company name, the advertiser
// name), without blanks or case-only repeats.
function advertiserNames(context) {
  const seen = new Set();
  const names = [];
  for (const name of Array.isArray(context?.names_tried) ? context.names_tried : []) {
    const text = typeof name === "string" ? name.trim() : "";
    if (text && !seen.has(text.toLowerCase())) {
      seen.add(text.toLowerCase());
      names.push(text);
    }
  }
  return names;
}

// Integer cents (or the currency's minor unit) as money, or null.
export function formatAmount(context) {
  const cents = context?.amount_cents;
  if (typeof cents !== "number") return null;
  const currency = typeof context.currency === "string" && context.currency ? context.currency.toUpperCase() : "USD";
  try {
    const fmt = currencyFormat(currency);
    return fmt.format(cents / 10 ** fmt.resolvedOptions().maximumFractionDigits);
  } catch {
    return `${cents} ${currency} (minor units)`;
  }
}

const card = { background: "#FFFFFF", border: "1px solid #E2E2E2", borderRadius: 14, boxShadow: "0 3px 15px rgba(0,0,0,0.08)", padding: "16px 20px", marginBottom: 14 };
const btnPrimary = { padding: "7px 14px", background: "#F90091", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" };
const btnGhost = { padding: "7px 14px", background: "transparent", color: "#3E3E3E", border: "1px solid #CFCFCF", borderRadius: 8, fontSize: 12, cursor: "pointer" };
const meta = { fontSize: 12, color: "#626262" };
const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 };

export function QuickBooksExceptionRow({ exception, onAct }) {
  const { reason_code: reason, context } = exception;
  const [confirming, setConfirming] = useState(null); // resolve | ignore | null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [chosen, setChosen] = useState("");

  // The database decides which actions a reason allows (exception_allowed_actions).
  const allowed = new Set(exception.allowed_actions ?? []);
  const candidates = allowed.has("choose_customer") && Array.isArray(context?.candidates)
    ? context.candidates.map((c) => ({ id: String(c.id), name: c.display_name || "Unnamed customer" }))
    : [];
  const amount = formatAmount(context);
  const names = advertiserNames(context);
  // Every name, none singled out: the first is the Stripe customer's, which can be a person.
  const knownAs = names.length > 0 ? `, known here as ${names.join(" or ")}` : "";

  const run = async (action, ...extra) => {
    setBusy(true);
    setError(null);
    try {
      await onAct(action, ...extra);
    } catch (err) {
      setError(err?.message || GENERIC_ACTION_ERROR);
      setConfirming(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-exception-row style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#151515" }}>{REASON_LABELS[reason] ?? reason}</div>
          <div style={{ ...meta, marginTop: 4, display: "flex", flexWrap: "wrap", gap: 12 }}>
            <span style={mono}>{exception.stripe_object_id}</span>
            <span>{exception.posting_kind}</span>
            <span>{fmtDateTime(exception.created_at)}</span>
            {amount && <span style={{ color: "#151515", fontWeight: 600 }}>{amount}</span>}
          </div>
        </div>
      </div>

      {exception.reason_detail && (
        <p style={{ fontSize: 13, color: "#3E3E3E", margin: "10px 0 0", lineHeight: 1.5 }}>{exception.reason_detail}</p>
      )}

      {reason === "customer_not_found" && (
        <p style={{ fontSize: 13, color: "#3E3E3E", margin: "10px 0 0", lineHeight: 1.5, background: "#FAFAFA", border: "1px solid #F0F0F0", borderRadius: 8, padding: "8px 12px" }}>
          {context?.advertiser_id != null ? (
            <>Ask accounting to add the line <code style={mono}>{`advertiser_id: ${context.advertiser_id}`}</code> to the Notes of the right QuickBooks customer{knownAs}. The next run links it.</>
          ) : (
            <>{`Ask accounting to create this customer in QuickBooks${knownAs},`} or to add an advertiser_id line to the Notes of the right QuickBooks customer. The next run links it.</>
          )}
        </p>
      )}

      {candidates.length > 0 && (
        <fieldset style={{ border: "1px solid #F0F0F0", borderRadius: 8, padding: "8px 12px", margin: "10px 0 0" }}>
          <legend style={{ ...meta, padding: "0 4px" }}>Choose the QuickBooks customer this Stripe customer is</legend>
          {candidates.map((c) => (
            <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#151515", padding: "3px 0", cursor: "pointer" }}>
              <input
                type="radio"
                name={`candidate-${exception.id}`}
                value={c.id}
                checked={chosen === c.id}
                onChange={() => setChosen(c.id)}
                disabled={busy}
              />
              {c.name} <span style={meta}>(QuickBooks id {c.id})</span>
            </label>
          ))}
          <button
            type="button"
            disabled={busy || !chosen}
            onClick={() => run("choose_customer", { qbo_customer_id: chosen })}
            style={{ ...btnPrimary, marginTop: 6, opacity: busy || !chosen ? 0.5 : 1, cursor: busy || !chosen ? "default" : "pointer" }}
          >
            {ACTIONS.choose_customer.label}
          </button>
        </fieldset>
      )}

      {confirming ? (
        <div style={{ marginTop: 12, background: "#FFF5FB", border: "1px solid #FFE9F6", borderRadius: 8, padding: "10px 12px" }}>
          <p style={{ fontSize: 13, color: "#151515", margin: "0 0 8px" }}>{`${ACTIONS[confirming].label}: ${ACTIONS[confirming].help} This cannot be undone.`}</p>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" disabled={busy} onClick={() => run(confirming)} style={btnPrimary}>
              {`Confirm ${ACTIONS[confirming].label.toLowerCase()}`}
            </button>
            <button type="button" disabled={busy} onClick={() => setConfirming(null)} style={btnGhost}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {["resolve", "ignore", "retry"].filter((a) => allowed.has(a)).map((a) => (
            <button
              key={a}
              type="button"
              disabled={busy}
              onClick={() => (ACTIONS[a].confirm ? setConfirming(a) : run(a))}
              style={btnGhost}
            >
              {ACTIONS[a].label}
            </button>
          ))}
        </div>
      )}

      {error && <p role="alert" style={{ fontSize: 12, color: "#B42318", margin: "8px 0 0" }}>{error}</p>}
    </div>
  );
}
